import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Field, inputCls } from "@/components/LegsEditor";
import { OFFICE_PHONE, ROLE_LABELS } from "@/lib/format";
import { useApi, errMsg } from "@/lib/api";

const BLANK = { name: "", email: "", title: "", phone: "", office_ext: "", role: "broker" };

export default function InviteUserDialog({ open, onOpenChange, roles, onSaved }) {
  const api = useApi();
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const options = roles.filter((r) => r !== "pending");

  useEffect(() => {
    if (open) setForm(BLANK);
  }, [open]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/users", form);
      toast.success(`Invitation emailed to ${form.email}`);
      onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#111319] border-white/10 max-w-lg" data-testid="invite-user-dialog">
        <DialogHeader>
          <p className="eyebrow">Invite</p>
          <DialogTitle className="font-serif-display text-3xl font-normal text-white">Add a portal user</DialogTitle>
        </DialogHeader>
        <form onSubmit={save} className="space-y-3 mt-2">
          <Field label="Full name"><Input className={inputCls} value={form.name} onChange={set("name")} required data-testid="invite-name-input" /></Field>
          <Field label="Email"><Input className={inputCls} type="email" value={form.email} onChange={set("email")} required data-testid="invite-email-input" /></Field>
          <Field label="Title (optional)"><Input className={inputCls} value={form.title} onChange={set("title")} maxLength={120} placeholder="e.g. Senior Charter Broker" data-testid="invite-title-input" /></Field>
          <div className="grid grid-cols-[1fr_120px] gap-3">
            <Field label="Mobile phone"><Input className={inputCls} type="tel" value={form.phone} onChange={set("phone")} required minLength={7} placeholder="+1 212 555 0100" data-testid="invite-phone-input" /></Field>
            <Field label="Office ext."><Input className={inputCls} value={form.office_ext} onChange={set("office_ext")} maxLength={20} placeholder="Optional" data-testid="invite-ext-input" /></Field>
          </div>
          <p className="text-[11px] text-zinc-500">Office line {OFFICE_PHONE} is shown for every user, with their ext. if set.</p>
          {options.length > 1 && (
            <Field label="Role">
              <Select value={form.role} onValueChange={set("role")}>
                <SelectTrigger className={inputCls} data-testid="invite-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>{options.map((r) => <SelectItem key={r} value={r} data-testid={`invite-role-${r}`}>{ROLE_LABELS[r]}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          )}
          <p className="text-[11px] text-zinc-500 pt-1">They'll get an email inviting them to create their account with this address. No password is sent by email.</p>
          <div className="flex justify-end gap-3 pt-3">
            <button type="button" className="frame-btn" onClick={() => onOpenChange(false)} data-testid="invite-cancel-btn">Cancel</button>
            <button type="submit" className="frame-btn gold" disabled={saving} data-testid="invite-submit-btn">{saving ? "Sending…" : "Send invitation"}</button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
